import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { api, ApiError } from "@/lib/api";

export type RoundsSort = "hot" | "new" | "top";
export type RoundTargetType = "thread" | "post";
export type RoundVote = -1 | 0 | 1;
export type RoundContentStatus =
  | "published"
  | "locked"
  | "hidden"
  | "deleted"
  | "auto_flagged";
export type ReportReason =
  | "patient_identifying"
  | "defamation"
  | "dosing_advice"
  | "commercial_spam"
  | "off_topic"
  | "hate_harassment"
  | "other";
export type ModerationResolution =
  | "noaction"
  | "hide"
  | "delete"
  | "lock"
  | "ban_community"
  | "ban_forum";

export interface RoundsStatus {
  open: boolean;
  memberCount: number;
  threshold: number;
  isAdmin: boolean;
  handleSet: boolean;
}

export interface RoundCommunity {
  id: string;
  slug: string;
  name: string;
  description: string;
  threadCount: number;
  lastActivityAt: string | null;
  subscribed: boolean;
}

export interface RoundAuthor {
  userId?: string;
  display: string;
  handle: string;
  role: string;
}

export interface RoundTeachingTile {
  id: string;
  title: string;
}

export interface RoundThreadSummary {
  id: string;
  communitySlug: string;
  communityName: string;
  title: string;
  excerpt: string;
  isAnonymisedCase: boolean;
  tags: string[];
  status: RoundContentStatus;
  score: number;
  replyCount: number;
  myVote: RoundVote;
  author: RoundAuthor;
  teachingTile: RoundTeachingTile | null;
  createdAt: string;
  lastActivityAt: string;
}

export interface RoundThread extends RoundThreadSummary {
  body: string;
  viewCount: number;
  lockedReason: string | null;
}

export interface RoundPost {
  id: string;
  parentPostId: string | null;
  depth: number;
  body: string;
  status: RoundContentStatus;
  score: number;
  myVote: RoundVote;
  author: RoundAuthor;
  createdAt: string;
  editedAt: string | null;
}

export interface RoundsCommunitiesResponse {
  communities: RoundCommunity[];
}

export interface RoundsThreadsResponse {
  threads: RoundThreadSummary[];
  nextCursor: string | null;
}

export interface RoundThreadResponse {
  thread: RoundThread;
  posts: RoundPost[];
}

export interface CreateRoundThreadBody {
  communitySlug: string;
  title: string;
  body: string;
  isAnonymisedCase: boolean;
  declaration: boolean;
  tags: string[];
}

export interface CreateRoundPostBody {
  body: string;
  parentPostId: string | null;
}

export interface RoundVoteBody {
  targetType: RoundTargetType;
  targetId: string;
  vote: RoundVote;
}

export interface RoundVoteResponse {
  ok: true;
  score: number;
  myVote: RoundVote;
}

export interface RoundReportBody {
  targetType: RoundTargetType;
  targetId: string;
  reason: ReportReason;
  note: string;
}

export interface AdminRoundReport {
  id: string;
  targetType: RoundTargetType;
  targetId: string;
  reason: ReportReason;
  note: string;
  reporter: { display: string };
  target: {
    title: string;
    excerpt: string;
    author: { display: string; handle: string };
    communitySlug: string;
  };
  createdAt: string;
  ageHours: number;
}

export interface AdminRoundsReportsResponse {
  items: AdminRoundReport[];
  slaHours: number;
}

export interface RoundsDigestPreferences {
  digestEnabled: boolean;
}

export type AdminRoundsDigestIneligibleReason =
  | "not_member"
  | "digest_off"
  | "no_subscriptions"
  | "nothing_new";

export interface AdminRoundsDigestPreview {
  eligible: boolean;
  reason: AdminRoundsDigestIneligibleReason | null;
  subject: string;
  html: string;
  text: string;
  threadCount: number;
}

export interface AdminRoundsDigestLogResponse {
  items: Array<{
    userId: string;
    display: string;
    sentAt: string;
    threadCount: number;
  }>;
  eligibleCount: number;
}

export function useRoundsStatus(citySlug: string) {
  return useQuery({
    queryKey: ["rounds-status", citySlug],
    queryFn: () =>
      api.get<RoundsStatus>(`/${citySlug}/v1/rounds/status`),
    enabled: Boolean(citySlug),
  });
}

export function useRoundsCommunities(citySlug: string, enabled = true) {
  return useQuery({
    queryKey: ["rounds-communities", citySlug],
    queryFn: () =>
      api.get<RoundsCommunitiesResponse>(
        `/${citySlug}/v1/rounds/communities`,
      ),
    enabled: enabled && Boolean(citySlug),
  });
}

export function useRoundsThreads(
  citySlug: string,
  community: string | undefined,
  sort: RoundsSort,
  enabled = true,
) {
  return useInfiniteQuery({
    queryKey: ["rounds-threads", citySlug, community ?? "all", sort],
    queryFn: ({ pageParam }) =>
      api.get<RoundsThreadsResponse>(`/${citySlug}/v1/rounds/threads`, {
        params: {
          community,
          sort,
          cursor: pageParam || undefined,
          limit: 20,
        },
      }),
    initialPageParam: "",
    getNextPageParam: (page) => page.nextCursor ?? undefined,
    enabled: enabled && Boolean(citySlug),
  });
}

export function useRoundThread(
  citySlug: string,
  threadId: string,
  enabled = true,
) {
  return useQuery({
    queryKey: ["rounds-thread", citySlug, threadId],
    queryFn: () =>
      api.get<RoundThreadResponse>(
        `/${citySlug}/v1/rounds/threads/${encodeURIComponent(threadId)}`,
      ),
    enabled: enabled && Boolean(citySlug && threadId),
  });
}

export function useCreateRoundThread(citySlug: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateRoundThreadBody) =>
      api.post<RoundThread>(`/${citySlug}/v1/rounds/threads`, body),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: ["rounds-threads", citySlug],
      });
      void queryClient.invalidateQueries({
        queryKey: ["rounds-communities", citySlug],
      });
      void queryClient.invalidateQueries({ queryKey: ["board-home", citySlug] });
    },
  });
}

export function useCreateRoundPost(citySlug: string, threadId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateRoundPostBody) =>
      api.post<RoundPost>(
        `/${citySlug}/v1/rounds/threads/${encodeURIComponent(threadId)}/posts`,
        body,
      ),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: ["rounds-thread", citySlug, threadId],
      });
      void queryClient.invalidateQueries({
        queryKey: ["rounds-threads", citySlug],
      });
    },
  });
}

export function useEditRoundThread(citySlug: string, threadId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: { title: string; body: string }) =>
      api.patch<RoundThread>(
        `/${citySlug}/v1/rounds/threads/${encodeURIComponent(threadId)}`,
        body,
      ),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: ["rounds-thread", citySlug, threadId],
      });
      void queryClient.invalidateQueries({
        queryKey: ["rounds-threads", citySlug],
      });
    },
  });
}

export function useEditRoundPost(
  citySlug: string,
  threadId: string,
  postId: string,
) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: { body: string }) =>
      api.patch<RoundPost>(
        `/${citySlug}/v1/rounds/posts/${encodeURIComponent(postId)}`,
        body,
      ),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: ["rounds-thread", citySlug, threadId],
      });
    },
  });
}

export function useRoundVote(citySlug: string, threadId?: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: RoundVoteBody) =>
      api.post<RoundVoteResponse>(`/${citySlug}/v1/rounds/vote`, body),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: ["rounds-threads", citySlug],
      });
      if (threadId) {
        void queryClient.invalidateQueries({
          queryKey: ["rounds-thread", citySlug, threadId],
        });
      }
    },
  });
}

export function useRoundReport(citySlug: string) {
  return useMutation({
    mutationFn: (body: RoundReportBody) =>
      api.post<{ ok: true }>(`/${citySlug}/v1/rounds/report`, body),
  });
}

export function useRoundCommunitySubscription(citySlug: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      slug,
      subscribed,
    }: {
      slug: string;
      subscribed: boolean;
    }) =>
      api.post<{ ok: true }>(
        `/${citySlug}/v1/rounds/communities/${encodeURIComponent(slug)}/subscribe`,
        { subscribed, notificationLevel: "weekly_digest" },
      ),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: ["rounds-communities", citySlug],
      });
    },
  });
}

export function useRoundsDigestPreferences(citySlug: string) {
  return useQuery({
    queryKey: ["rounds-digest-preferences", citySlug],
    queryFn: () =>
      api.get<RoundsDigestPreferences>(
        `/${citySlug}/v1/rounds/digest-preferences`,
      ),
    enabled: Boolean(citySlug),
  });
}

export function useUpdateRoundsDigestPreferences(citySlug: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (preferences: RoundsDigestPreferences) =>
      api.patch<RoundsDigestPreferences>(
        `/${citySlug}/v1/rounds/digest-preferences`,
        preferences,
      ),
    onSuccess: (preferences) => {
      queryClient.setQueryData<RoundsDigestPreferences>(
        ["rounds-digest-preferences", citySlug],
        preferences,
      );
    },
  });
}

export function useAdminRoundsReports() {
  return useQuery({
    queryKey: ["admin-rounds-reports", "pending"],
    queryFn: () =>
      api.get<AdminRoundsReportsResponse>("/v1/admin/rounds/reports", {
        params: { status: "pending" },
      }),
  });
}

export function useAdminRoundsDigestPreview() {
  return useMutation({
    mutationFn: (userId: string) =>
      api.get<AdminRoundsDigestPreview>("/v1/admin/rounds-digest/preview", {
        params: { userId },
      }),
  });
}

export function useAdminRoundsDigestLog(weekStart: string) {
  return useQuery({
    queryKey: ["admin-rounds-digest-log", weekStart],
    queryFn: () =>
      api.get<AdminRoundsDigestLogResponse>("/v1/admin/rounds-digest/log", {
        params: { weekStart },
      }),
    enabled: Boolean(weekStart),
  });
}

export function useResolveAdminRoundReport() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      action,
      note,
    }: {
      id: string;
      action: ModerationResolution;
      note: string;
    }) =>
      api.post<{ ok: true }>(
        `/v1/admin/rounds/reports/${encodeURIComponent(id)}/resolve`,
        { action, note },
      ),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: ["admin-rounds-reports"],
      });
    },
  });
}

export function useSetRoundsOpen(citySlug: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (roundsOpen: boolean) =>
      api.patch<{ ok: true }>("/v1/admin/rounds/flags", { roundsOpen }),
    onSuccess: (_response, roundsOpen) => {
      queryClient.setQueryData<RoundsStatus>(
        ["rounds-status", citySlug],
        (current) => (current ? { ...current, open: roundsOpen } : current),
      );
    },
  });
}

export function useReplyAsAdmin() {
  return useMutation({
    mutationFn: ({
      threadId,
      body,
      attributedTo,
    }: {
      threadId: string;
      body: string;
      attributedTo: string;
    }) =>
      api.post<RoundPost>(
        `/v1/admin/rounds/threads/${encodeURIComponent(threadId)}/reply-as-admin`,
        { body, attributedTo },
      ),
  });
}

export function roundsErrorCode(error: unknown): string | null {
  return error instanceof ApiError ? error.code : null;
}

export function isIdentifierGuardError(error: unknown): boolean {
  return roundsErrorCode(error) === "rounds.identifier_detected";
}

export function roundsErrorMessage(error: unknown): string {
  switch (roundsErrorCode(error)) {
    case "rounds.declaration_required":
      return "Confirm the anonymised-case declaration before posting.";
    case "rounds.handle_required":
      return "Finish your member setup before posting in Rounds.";
    case "rounds.banned":
      return "Your Rounds posting access is currently restricted.";
    case "rounds.closed":
      return "Rounds is not open yet.";
    default:
      return "This could not be saved. Check your connection and try again.";
  }
}

export function canEditRoundContent(
  createdAt: string,
  authorHandle: string,
  currentHandle: string | undefined,
  now = Date.now(),
): boolean {
  if (!currentHandle || authorHandle !== currentHandle) return false;
  const created = new Date(createdAt).getTime();
  return Number.isFinite(created) && now - created <= 30 * 60 * 1_000;
}

export function formatRoundsTime(value: string, now = Date.now()): string {
  const date = new Date(value);
  const time = date.getTime();
  if (!Number.isFinite(time)) return value;
  const minutes = Math.max(0, Math.floor((now - time) / 60_000));
  if (minutes < 1) return "just now";
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d ago`;
  return new Intl.DateTimeFormat("en-AU", {
    day: "numeric",
    month: "short",
    year: date.getFullYear() === new Date(now).getFullYear() ? undefined : "numeric",
  }).format(date);
}
